import React, { useState } from "react";
import { Sparkles, CheckCircle2, Calendar, ArrowRight, ChevronDown, ChevronUp } from "lucide-react";

interface ChangelogEntry {
  month: string; // e.g., "November 2025"
  version?: string;
  features: string[];
  improvements: string[];
  fixes: string[];
}

// Changelog data grouped by month
const CHANGELOG: ChangelogEntry[] = [
  {
    month: "November 2025",
    version: "Latest",
    features: [
      "Easy contact form with feedback and bug reporting",
      "Keyboard shortcuts for cut and delete operations",
      "Text outlines for better text visibility",
      "What's New section with changelog",
      "Video stacking capabilities in composer",
      "Video animations support",
      "Fill screen options in horizontal and vertical stacks",
      "Animation preview in video editor",
      "Prompt support and audio file uploads for captioning",
    ],
    improvements: [
      "Package updates and dependency management",
      "Consolidated thumbnail and banner designers",
      "Audio properties improvements",
      "Render progress indicators with spinners",
      "Global progress tracking",
      "Time display fixed to 2 decimal places",
      "Better memory management with lag introduction",
      "Reduced log clutter",
      "Temporary file cleanup in FFmpeg",
      "UI updates and mobile fixes",
      "New font options added",
    ],
    fixes: [
      "Fixed render issues in advanced composer",
      "Fixed loop issues in composer v2",
      "Fixed play button functionality",
      "Fixed reupload after delete bug",
      "Fixed FFmpeg issues in stack composer",
      "Fixed styling issues in captioning",
      "Fixed routing and logo display issues",
    ],
  },
  {
    month: "October 2025",
    version: "Previous",
    features: [
      "Advanced Video Composer v3 with timeline editor",
      "Multi-track audio support",
      "Video transitions and overlays",
      "Simple Video Stack Composer",
      "Shorts Clipper for extracting multiple clips",
      "Auto Captions with AI transcription",
      "Thumbnail Creator",
      "Banner Creator",
      "Image to Video converter",
    ],
    improvements: [
      "Improved video processing performance",
      "Better UI/UX across all tools",
      "Enhanced export options",
    ],
    fixes: [
      "Various bug fixes and stability improvements",
    ],
  },
];

export function WhatsNew() {
  const [expandedMonths, setExpandedMonths] = useState<Set<number>>(new Set([0])); // First month expanded by default

  const toggleMonth = (index: number) => {
    setExpandedMonths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-surface-base to-surface-elevated">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-4">
            <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-accent-primary" />
            <h1 className="text-4xl sm:text-5xl font-bold text-text-primary">
              What's New
            </h1>
          </div>
          <p className="text-lg text-text-secondary mt-4">
            Stay updated with the latest features, improvements, and fixes
          </p>
        </div>

        {/* Coming Soon Section - Moved to Top */}
        <div className="mb-12 p-6 bg-gradient-to-r from-accent-primary/10 to-accent-secondary/10 rounded-xl border border-border-default">
          <h3 className="text-xl font-semibold text-text-primary mb-2 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-accent-primary" />
            Coming Soon
          </h3>
          <p className="text-text-secondary">
            We're working on exciting new features! Check back soon for updates on our roadmap.
          </p>
        </div>

        {/* Changelog Entries - Grouped by Month */}
        <div className="space-y-4">
          {CHANGELOG.map((entry, index) => {
            const isExpanded = expandedMonths.has(index);
            return (
              <div
                key={index}
                className="bg-surface-elevated rounded-xl border border-border-default shadow-lg overflow-hidden"
              >
                {/* Entry Header - Clickable */}
                <button
                  onClick={() => toggleMonth(index)}
                  className="w-full bg-gradient-to-r from-accent-primary/10 to-accent-secondary/10 px-6 py-4 border-b border-border-default hover:from-accent-primary/15 hover:to-accent-secondary/15 transition-colors text-left"
                >
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-accent-primary" />
                      <h2 className="text-2xl font-bold text-text-primary">
                        {entry.month}
                      </h2>
                      {entry.version && (
                        <span className="px-3 py-1 bg-accent-primary/20 text-accent-primary rounded-full text-sm font-semibold">
                          {entry.version}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-text-secondary">
                      <span className="text-sm">
                        {isExpanded ? 'Collapse' : 'Expand'}
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </div>
                  </div>
                </button>

                {/* Entry Content - Collapsible */}
                {isExpanded && (
                  <div className="p-6 space-y-6">
                    {/* New Features */}
                    {entry.features.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-text-primary mb-3 flex items-center gap-2">
                          <Sparkles className="w-5 h-5 text-accent-primary" />
                          New Features
                        </h3>
                        <ul className="space-y-2">
                          {entry.features.map((feature, idx) => (
                            <li
                              key={idx}
                              className="flex items-start gap-3 text-text-secondary"
                            >
                              <CheckCircle2 className="w-5 h-5 text-accent-primary flex-shrink-0 mt-0.5" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Improvements */}
                    {entry.improvements.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-text-primary mb-3 flex items-center gap-2">
                          <ArrowRight className="w-5 h-5 text-accent-secondary" />
                          Improvements
                        </h3>
                        <ul className="space-y-2">
                          {entry.improvements.map((improvement, idx) => (
                            <li
                              key={idx}
                              className="flex items-start gap-3 text-text-secondary"
                            >
                              <div className="w-5 h-5 flex-shrink-0 mt-0.5 flex items-center justify-center">
                                <div className="w-2 h-2 rounded-full bg-accent-secondary" />
                              </div>
                              <span>{improvement}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Bug Fixes */}
                    {entry.fixes.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-text-primary mb-3 flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                          Bug Fixes
                        </h3>
                        <ul className="space-y-2">
                          {entry.fixes.map((fix, idx) => (
                            <li
                              key={idx}
                              className="flex items-start gap-3 text-text-secondary"
                            >
                              <div className="w-5 h-5 flex-shrink-0 mt-0.5 flex items-center justify-center">
                                <div className="w-2 h-2 rounded-full bg-green-500" />
                              </div>
                              <span>{fix}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
